/**
 * Text of the course with its notes, signs and terms set apart.
 *
 * `RichText` is for a single line (a title, a caption, a question); `RichMarkdown`
 * (in its own file, so that only pages with theory load the Markdown renderer) for
 * theory written in Markdown. Both mark the same things the same way. A marked term
 * explains itself wherever that does no harm, which is the caller's to say:
 *
 * - `hints`: in running text, titles and captions. The term takes focus, and its
 *   hint leads on to the glossary;
 * - `hints="hover"`: inside a link or a button. The term cannot take focus there (the
 *   link does) and the hint cannot hold a link of its own, so it only says what the word means;
 * - no hints: in a question or an answer option, where a definition would give the answer away.
 *
 * The learner can turn the marks off altogether (settings): terms are then plain words.
 */
import { useMemo, type ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';
import { Tooltip } from '@/shared/ui/Tooltip';

import { useMusicFormatter, useTermLink, useTermMarks } from '../i18n/MusicI18n';
import type { Segment, TermRef } from './segments';

/** What a hint over a term says: the term, its sign or abbreviation, what it means. */
export function TermCard({ term, plain = false }: { term: TermRef; plain?: boolean }) {
  const termLink = useTermLink();
  // Inside a link there is no room for another one.
  const link = plain ? null : termLink;
  return (
    <span className="block space-y-1">
      <span className="block font-semibold">
        {term.name}
        {term.short && term.short !== term.name ? (
          <span className="ml-2 font-normal text-muted">{term.short}</span>
        ) : null}
      </span>
      <span className="block">{term.definition}</span>
      {link?.(term.key)}
    </span>
  );
}

/** Whether a term explains itself: fully, to the pointer only, or not at all. */
export type Hints = boolean | 'hover';

interface TermMarkProps {
  term: TermRef;
  hints: Hints;
  children: ReactNode;
}

/** A term of the glossary as it stands in a text. */
export function TermMark({ term, hints, children }: TermMarkProps) {
  const marked = useTermMarks();
  const sign = term.category === 'accidentals';
  const className = cn(sign ? 'music-sign' : 'music-term', hints && 'cursor-help');
  if (!marked) return <>{children}</>;
  if (!hints || !term.definition) {
    return (
      <span className={className} data-term={term.key}>
        {children}
      </span>
    );
  }
  return (
    <Tooltip content={<TermCard term={term} plain={hints === 'hover'} />} focusable={hints !== 'hover'} className={className} data-term={term.key}>
      {children}
    </Tooltip>
  );
}

export function NoteMark({ children }: { children: ReactNode }) {
  return <span className="music-note">{children}</span>;
}

function SegmentView({ segment, hints }: { segment: Segment; hints: Hints }) {
  if (segment.kind === 'note') return <NoteMark>{segment.text}</NoteMark>;
  if (segment.kind === 'term') {
    return (
      <TermMark term={segment.term} hints={hints}>
        {segment.text}
      </TermMark>
    );
  }
  return <>{segment.text}</>;
}

interface RichTextProps {
  text: string;
  /** How terms explain themselves. Off by default: see the note above. */
  hints?: Hints;
}

/** One line of text with its music tokens rendered and its terms marked. */
export function RichText({ text, hints = false }: RichTextProps) {
  const formatter = useMusicFormatter();
  const segments = useMemo(() => formatter.segments(text), [formatter, text]);
  return (
    <>
      {segments.map((segment, index) => (
        <SegmentView key={index} segment={segment} hints={hints} />
      ))}
    </>
  );
}
